import { useModal, Loader } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useRef, useEffect } from 'react';
import { TranslatorContext } from '@jutro/locale';
import ContactService from '../../../../../capabilities/twia-capability-quoteandbind-residential/services/ContactService.js';
import AdditionalInterestDetails from '../AdditionalInterestDetails/AdditionalInterestDetails.jsx';
import messages from './AdditionalInterestComponent.messages.js';
import metadata from './AdditionalInterestComponent.metadata.json5';
import styles from '../AdditionalInterestComponent.module.scss';

import { Button } from '@jutro/legacy/components';

function AdditionalInterestComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate } = props;
    const { authHeader } = useAuthentication();
    const [isLoading, setLoadingState] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const [additionalInfoVM, setAdditionalInfoVM] = useState();
    const [showAdditionalInterest, setShowAdditionalInterest] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const mailingAddressTypeCode = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.AddressType').getCode('mailingExt');
    const usCountryCode = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.Country').getCode('US');
    const policyContactDetailsDTO = 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO';
    const { refreshData } = useDataRefresh();
    const [additionalInterest, setAdditionalInterest] = useState([]);
    const additionalInterestData = useRef(additionalInterest);
    const policyTypeCode = _.get(submissionVM, 'lobData.homeowners.policyType_TWIA.value.code');
    const translator = useContext(TranslatorContext);
    const selectedUWCompany = _.get(submissionVM, 'baseData.associationName_TWIA.value.code');

    useEffect(() => {
        additionalInterestData.current = _.filter(submissionVM.baseData.policyContactDetails_TWIA?.value, 
            pcd => pcd.policyContactRoles[0].subtype === 'PolicyAddlInterest' && pcd.policyContactRoles[0].policyAdditionalInterest?.additionalInterestDetails.length > 0);
    })

    const addAdditionalInterest = useCallback(() => {

        const newContact = viewModelService.create({}, 'pc', 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO')

        newContact.active = true;
        newContact.contactType_TWIA.value = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.ContactType').getCode('person');


        newContact.primaryAddress.value = {};
        newContact.primaryAddress.addressType.value = mailingAddressTypeCode
        newContact.primaryAddress.country.value = usCountryCode;
        newContact.primaryAddress.mustStandardize_Ext.value = true;
        newContact.subtype.value = 'Person';

        const pcr = { subtype: 'PolicyAddlInterest' };

        pcr.accountContactRole = { subtype: 'AdditionalInterest' };

        const policyType = submissionVM.lobData.homeowners.policyType_TWIA.value.code;
        const additionalInterestDetail = { policyType_TWIA: policyType, subtype: 'HODwellingAddlInt_HOE' };

        pcr.policyAdditionalInterest = { additionalInterestDetails: [additionalInterestDetail] };

        newContact.policyContactRoles.value = [pcr];
        setAdditionalInfoVM(() => newContact)
        setShowAdditionalInterest(true)
        // OnNext will not call again if there are no changes to the model so setting dummy tag.
        _.set(submissionVM, 'isEditingAdditionalInterest', true)
        updateWizardData(submissionVM)
    }, [])

    const oldVMData = [];
    const saveRepairUpdates = async (updatedData) => {
        const newItem = [...additionalInterestData.current, updatedData.value];

        await saveAdditionalInterest()
        updateWizardData(submissionVM);        
    };

    const cancelRepairUpdates = () => {
        setAdditionalInfoVM(oldVMData);
        setShowAdditionalInterest(false);
    };

    const getAdditionalInterestDetail = (addlInterest) => {

        const pcrs = _.get(addlInterest, 'policyContactRoles[0]');

        if (pcrs !== undefined) {
            if (pcrs.subtype === 'PolicyAddlInterest') {
                return _.get(pcrs, 'policyAdditionalInterest.additionalInterestDetails[0]');
            }
        }

        return undefined;
    }

    const getSelectedPositions = () => {
        // DE11069 filter empty positions for non-mortgagee AIs
        return _.filter(additionalInterestData.current.map((ai) => {
            const aiDetail = getAdditionalInterestDetail(ai);
            return aiDetail.mortgagePosition_TWIA;
        }), item => { return item !== undefined });
    }

    const getPriority = (priority) => {

        switch (priority) {
            case 'FirstMortgagee': return 5;
            case 'SecondMortgagee': return 10;
            case 'ThirdMortgagee': return 15;
            default: return 0;
        }
    }
    
    // Validate mortgage positions making sure they are ordered sequentially.This will avoid quote corrupt
    const validatePositions = (addInterest, remove) => {
        
        const mortgagePositions = getSelectedPositions();
        let sum = _.sumBy(mortgagePositions, a => (a != null ? getPriority(a) : 0));

        if (addInterest) {
            const aiDetail = getAdditionalInterestDetail(addInterest);

            if (aiDetail.mortgagePosition_TWIA) {
                if (remove) {
                    sum -= getPriority(aiDetail.mortgagePosition_TWIA);
                } else {
                    sum += getPriority(aiDetail.mortgagePosition_TWIA);
                }
            }
        }

        if (sum === 0 || sum === 5 || (sum === 15 && mortgagePositions.length > 0) || sum === 30) {
            return true;
        }

        return false;
    }

    const getJobID = (job) => job.quoteID ? job.quoteID.value : job.jobID.value

    const saveAdditionalInterest = async () => {
        setLoadingState(true);
        if (validatePositions(additionalInfoVM.value, false)) {

        await ContactService.createPolicyContact(getJobID(submissionVM), additionalInfoVM.value, authHeader).then((newContact) => {

            if (newContact) {
                const contactDetailsDTO = viewModelService.create(newContact, 'pc', policyContactDetailsDTO);
                const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

                if (policyContactDetails.value == null) {

                    policyContactDetails.value = {};
                }

                policyContactDetails.value.push(contactDetailsDTO.value);
                additionalInterestData.current.push(contactDetailsDTO.value)
                updateWizardData(submissionVM)
            }
        })
            .catch(() => {
                showAlert({
                    title: messages.saveFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });
            setShowAdditionalInterest(false);
        } else {
            showAlert({
                title: messages.mortgageValidationTitle,
                message: messages.mortgageValidationMsg,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.close,
            }).catch(_.noop);
        }
        setLoadingState(false);
    }


    const deleteAdditionalInterest = async (aiPublicID, item) => {

        if (validatePositions(item, true)) {
        await ContactService.removePolicyContact(getJobID(submissionVM), aiPublicID, authHeader).then(() => {

            const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

            if (policyContactDetails.value != null) {

                policyContactDetails.value = _.filter(policyContactDetails.value, pcd => pcd.publicID !== aiPublicID);
                additionalInterestData.current = additionalInterestData.current.filter((ai) => ai.publicID !== aiPublicID);
                setAdditionalInterest(additionalInterestData.current)
            }
        })
            .catch(() => {
                showAlert({
                    title: messages.saveFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });
        } else {
            showAlert({
                title: messages.mortgageValidationTitle,
                message: messages.mortgageValidationMsg,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: messages.close,
            }).catch(_.noop);
        }

    }

    const deleteRow = async (item, index) => {
        showConfirm({
            title: messages.removeAdditionalInterest,
            message: messages.removeAddlInterestMessage,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok,
            cancelButtonText: messages.cancel,
            className: styles.jut__Button__filled,
        }).then(async (results) => {

            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }

            const aiPublicID = item.publicID;

            await deleteAdditionalInterest(aiPublicID, item);
            updateWizardData(submissionVM);

            return true;
        }, _.noop)
    };

    const formatInterestType = (type) => {

        switch (type) {
            case 'CONSALE': return translator(messages.contractofSale);
            case 'LOSSP': return translator(messages.lossPayee);
            case 'MORTGAGEE': return translator(messages.mortgagee);
            case 'PREMFINANCE_TWIA': return translator(messages.premiumFinance);
            default: return type;
        }
    }

    const formatMortgageType = (type) => {

        switch (type) {
            case 'FirstMortgagee': return translator(messages.firstMortgagee);
            case 'SecondMortgagee': return translator(messages.secondMortgagee);
            case 'ThirdMortgagee': return translator(messages.thirdMortgagee);
            default: return type;
        }
    }

    const getName = (item) => item.subtype === 'Person' ? item.firstName + " " + item.lastName : item.contactName;

    const getInterestType = (item) => {
        if (!item && !item.policyContactRoles[0].policyAdditionalInterest.additionalInterestDetails[0].type) {
            return undefined;
        }

        return formatInterestType(item.policyContactRoles[0].policyAdditionalInterest.additionalInterestDetails[0].type);
    }

    const getLoanNumber = (item) => item.policyContactRoles[0].policyAdditionalInterest.additionalInterestDetails[0].contractNumber;

    const getMortgagePosition = (item) => {

        if (!item && !item.policyContactRoles[0].policyAdditionalInterest.additionalInterestDetails[0].mortgagePosition_TWIA) {
            return undefined
        }

        return formatMortgageType(item.policyContactRoles[0].policyAdditionalInterest.additionalInterestDetails[0].mortgagePosition_TWIA)
    }
    const actionButtons = (item, index) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                className={styles.buttons}
                type="text"
                icon="gw-delete"
                onClick={() => deleteRow(item, index)}
                aria-label="Delete Row"
            />
        </div>
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        add: {
            visible: !showAdditionalInterest
        },
        additionalInterestSection: {
            visible: showAdditionalInterest,
            item: additionalInfoVM,
            saveData: saveRepairUpdates,
            cancelData: cancelRepairUpdates,
            isInvalid: findInvalidComponent,
            showErrors,
            onValidate,
            companyType: selectedUWCompany
        },
        additionalInterestTable: {
            data: additionalInterestData.current
        },
        mortgagePositionColumn: {
            visible: policyTypeCode !== 'TWRP'
        }

    };

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, `${path}.value`, value);
            updateWizardData(submissionVM);
            refreshData();
        },
        [submissionVM, refreshData, updateWizardData]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: { addtionalInterestDetailsComponent: AdditionalInterestDetails },
        resolveCallbackMap: {
            onAdd: addAdditionalInterest,
            getName,
            getInterestType,
            getMortgagePosition,
            getLoanNumber,
            actionButtons
        },
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={submissionVM}
            overrideProps={overrideProps}
            onModelChange={updateWizardData}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValueChange={writeValue}
        />
    );
}

export default AdditionalInterestComponent;
